import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import React, { useEffect, useMemo } from 'react';
import {
  selectPromotionBySlug,
  selectPromotionsError,
  selectPromotionsInProgress,
} from '@/store/cms/promotions/selectors';
import { useParams } from 'react-router-dom';
import { RootState } from '@/models/root-state.model';
import PromotionContent from '@/pages/promotion/PromotionContent';
import PromotionLoading from '@/pages/promotion/PromotionLoading';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import PromotionError from '@/pages/promotion/PromotionError';
import { useRestrictedMode } from '@/hooks/useRestrictedMode';
import PromotionsRestrictedNotice from '@/pages/promotions/PromotionsRestrictedNotice';
import { selectIsThailand } from '@/store/selectors/commonSelectors';
import { envIsSweden } from '@/constants/constants';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';

function PromotionPage() {
  const intl = useIntl();
  const promotionsError = useSelector(selectPromotionsError);
  const promotionsLoading = useSelector(selectPromotionsInProgress);
  const { slug } = useParams();
  const promotion = useSelector((state: RootState) => selectPromotionBySlug(state, slug));
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isRestricted = useRestrictedMode();
  const isThailand = useSelector(selectIsThailand);
  const navigate = useAbsoluteNavigate();

  // Only the restricted markets without promotions (Thailand, Sweden) redirect home; Alberta
  // (ROC) keeps its Alberta-specific notice. Non-restricted markets are unaffected.
  const redirectToHome = isRestricted && (isThailand || envIsSweden());

  useEffect(() => {
    if (redirectToHome) {
      navigate('/');
    }
  }, [redirectToHome, navigate]);

  const VisibleComponent = useMemo(() => {
    if (isRestricted) {
      return <PromotionsRestrictedNotice />;
    }

    if (promotionsLoading) {
      return <PromotionLoading />;
    }

    if (!promotion || promotionsError) {
      return <PromotionError />;
    }

    return <PromotionContent promotion={promotion} />;
  }, [promotionsError, promotionsLoading, promotion, isAuthenticated, isRestricted]);

  if (redirectToHome) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>
          LuckyDays - {intl.formatMessage({ id: 'promotions.header' })} -{' '}
          {promotion ? promotion.title : intl.formatMessage({ id: '404.header' })}
        </title>
      </Helmet>
      <div className="pt-8 md:pt-24">{VisibleComponent}</div>
    </>
  );
}

export default PromotionPage;
